import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { usePositionContext } from '../contexts/PositionContext';
import { useFullChartView } from '../contexts/FullChartViewContext';
import { usePanelPosition } from '../contexts/PanelPositionContext';
import LivePrice from './LivePrice';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TrendingUp, TrendingDown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import MarketTicker from './MarketTicker';
import LivePriceBig from './LivePriceBig';
import LivePriceRight from './LivePriceRight';
import TimeframePanel from './TimeframePanel';
import TradingForm from './TradingForm';
import TradingChart from './TradingChart';
import PortfolioSummary from './PortfolioSummary';
import useBinanceMarketData from '../hooks/useBinanceMarketData';
import { MarketData } from '../services/binanceService';
import { formatPrice } from '../utils/priceFormatter';
import RightPanel from './RightPanel';
import ToastContainer from './ToastContainer';
import PositionPanel from './PositionPanel';
import OrderBook from './OrderBook';
import MarketDepth from './MarketDepth';
import TechnicalSentiments from './TechnicalSentiments';
import LeftPanel from './LeftPanel';
import SortablePanel from './SortablePanel';
import { useIsMobile } from '../hooks/useIsMobile';

interface Panel {
  id: string;
  title: string;
  component: React.ReactNode;
}


const ReorderableLayout: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  const { isFullChartView } = useFullChartView();
  const { isPanelOnRight } = usePanelPosition();
  // const [selectedSymbol, setSelectedSymbol] = useState('BTCUSDT');
  const [selectedSymbol, setSelectedSymbol] = useState('ETHUSDT');
  const [timeframe, setTimeframe] = useState('1m');
  const [chartType, setChartType] = useState('candlestick');
     const [selectedMarket, setSelectedMarket] = useState('Crypto');
  const [currentPrice, setCurrentPrice] = useState('0.00');
  const [priceChange, setPriceChange] = useState('+0.00%');
  const [isPricePositive, setIsPricePositive] = useState(true);
  const [klinePrice, setKlinePrice] = useState('0.00');
  const [klineVolume, setKlineVolume] = useState(0);
  const priceRef = useRef<number | null>(null);
  const [priceColor, setPriceColor] = useState('text-black');
  const [isPositionsExpanded, setIsPositionsExpanded] = useState(false);
  const [positionPanelHeight, setPositionPanelHeight] = useState(60); // Default height in pixels
  const [isDragging, setIsDragging] = useState(false);
  const [isPositionPanelFullscreen, setIsPositionPanelFullscreen] = useState(false);
  const [hasBeenManuallyResized, setHasBeenManuallyResized] = useState(false);
  const [timeframeIndex, setTimeframeIndex] = useState(0); // Track current timeframe position
  const [showArrows, setShowArrows] = useState(false); // Track if arrows should be shown
  const dragRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  // const { theme } = useTheme(); // This line is now redundant as theme is passed as a prop

  // Get position data for dynamic height calculation
  const { openPositions } = usePositionContext();

  // Reset manual resize state when panel is toggled programmatically
  const handlePositionPanelToggle = useCallback((expanded: boolean) => {
    if (!isDragging) {
      setHasBeenManuallyResized(false);
      setIsPositionsExpanded(expanded);
    }
  }, [isDragging]);

  // Calculate dynamic heights based on position panel state
  const calculateHeights = useMemo(() => {
    if (isPositionPanelFullscreen) {
      // Fullscreen mode - position panel takes full height from timeframe header down
      return {
        positionHeight: 'calc(100vh - 90px)', // Full viewport height minus header space
        positionMaxHeight: 'calc(100vh - 90px)'
      };
    } else if (isPositionsExpanded) {
      // Expanded mode - use the manually resized height if available, otherwise calculate default
      const availableHeight = window.innerHeight - 200; // Total available height minus headers
      const defaultExpandedHeight = Math.max(availableHeight * 0.15, 185); // 15% or minimum 185px
      const finalHeight = hasBeenManuallyResized ? positionPanelHeight : defaultExpandedHeight;
      
      return {
        positionHeight: `${finalHeight}px`,
        positionMaxHeight: `${finalHeight}px`
      };
    } else {
      // Collapsed mode - minimal position panel height
      const collapsedHeight = 60; // Just enough for headers
      
      return {
        positionHeight: `${collapsedHeight}px`,
        positionMaxHeight: `${collapsedHeight}px`
      };
    }
  }, [isPositionsExpanded, isPositionPanelFullscreen, positionPanelHeight, hasBeenManuallyResized]);

  // Drag handlers for resizing position panel
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = true;
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current) return;
    
    // Cancel previous animation frame if it exists
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!dragRef.current) return;
      
      // Calculate the available height for the position panel
      const viewportHeight = window.innerHeight;
      const headerHeight = 90; // Approximate header height
      const bottomPadding = 60; // Bottom padding
      const availableHeight = viewportHeight - headerHeight - bottomPadding;
      
      // Calculate new height based on mouse position from bottom
      const newHeight = viewportHeight - e.clientY - bottomPadding;
      
      // Set constraints
      const minHeight = 60; // Minimum height for position panel
      const maxHeight = Math.floor(viewportHeight * 0.55); // Allow up to 60% of screen height
      
      // Apply constraints and update height
      const constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
      setPositionPanelHeight(constrainedHeight);
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current = false;
    setIsDragging(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    // Mark as manually resized and persist the dragged height
    setHasBeenManuallyResized(true);
    if (positionPanelHeight > 100) {
      setIsPositionsExpanded(true);
    } else {
      setIsPositionsExpanded(false);
    }
  }, [positionPanelHeight]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      // Add a class to body to indicate dragging state
      document.body.classList.add('resizing');
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Remove the dragging class
      document.body.classList.remove('resizing');
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('resizing');
      // Clean up animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      document.body.classList.remove('resizing');
    };
  }, []);

  // Use Binance market data hook for consistent data across all components
  const {
    isLoading: isMarketDataLoading,
    error: marketDataError,
    isConnected: isBinanceConnected,
    getMarketDataForSymbol,
    getMarketDataByCategory,
  } = useBinanceMarketData();

  // Get current symbol data from Binance
  const currentSymbolData = getMarketDataForSymbol(selectedSymbol);

  // Update price display when symbol data changes
  // This is the STABLE source for percentage changes - prevents flickering
  useEffect(() => {
    if (currentSymbolData) {
      setCurrentPrice(currentSymbolData.price);
      setPriceChange(currentSymbolData.change);
      setIsPricePositive(currentSymbolData.isPositive);
    }
  }, [currentSymbolData]);

  // Track price changes for color updates
  useEffect(() => {
    const numericCurrentPrice = parseFloat(currentPrice);
    if (!isNaN(numericCurrentPrice) && priceRef.current !== null) {
      if (numericCurrentPrice > priceRef.current) {
        setPriceColor('text-[#2AB7A6]');
      } else if (numericCurrentPrice < priceRef.current) {
        setPriceColor('text-[#e81d25]');
      } else {
        setPriceColor(theme === 'light' ? 'text-black' : 'text-white');
      }
    }
    // Update ref for next comparison
    priceRef.current = numericCurrentPrice;
  }, [currentPrice]);

  // Get market data for different categories
  const getMarketDataForCategory = (category: string): MarketData[] => {
    switch (category) {
      case 'Crypto':
        return getMarketDataByCategory('crypto');
      case 'Forex':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include forex pairs
        return [];
      case 'Commodities':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include commodities
        return [];
      case 'Stocks':
        // For now, return empty array as we're only handling crypto
        // Can be expanded later to include stocks
        return [];
      default:
        return getMarketDataByCategory('crypto');
    }
  };


  // Real-time price updates will come from API responses
  // No simulation - prices are updated when real data is received

  // Function to handle kline price updates from TradingChart
  const handleKlinePriceUpdate = useCallback((formattedPrice: string, closePrice: number, volume: number) => {
    setKlinePrice(formattedPrice);
    setKlineVolume(volume);
  }, []);



  const [leftPanelsOrder, setLeftPanelsOrder] = useState<string[]>(['markets', 'live-price', 'buy-sell']);

  // Market data for symbol selection - now using real Binance data
  const marketDataByCategory = {
    Crypto: getMarketDataForCategory('Crypto'),
    Forex: getMarketDataForCategory('Forex'),
    Commodities: getMarketDataForCategory('Commodities'),
    Stocks: getMarketDataForCategory('Stocks'),
  };

  // Get all market data for search functionality
  const allMarketData = Object.values(marketDataByCategory).flat();

  // The callback needs the freshest market data, but depending on it directly would give
  // the callback a new identity on every ticker batch, rebuilding the left panel's element
  // tree several times a second. Reading through a ref keeps the identity stable.
  const allMarketDataRef = useRef<MarketData[]>([]);
  allMarketDataRef.current = allMarketData;

  // Function to handle symbol selection and update prices
  // This also updates the STABLE percentage change data
  const handleSymbolSelect = useCallback((symbol: string) => {
    setSelectedSymbol(symbol);

    // Get the current price for the selected symbol from market data
    const selectedAsset = allMarketDataRef.current.find((item: any) => item.symbol === symbol);

    if (selectedAsset) {
      // Update the current price and change to match the selected asset
      // This ensures stable percentage display when switching symbols
      setCurrentPrice(selectedAsset.price);
      setPriceChange(selectedAsset.change);
      setIsPricePositive(selectedAsset.isPositive);
    }
  }, []);

  // Filter market data based on search (if needed)
  const getFilteredMarketData = (category: string, searchTerm: string = '') => {
    const data = marketDataByCategory[category as keyof typeof marketDataByCategory] || [];
    if (!searchTerm) return data;
    
    return data.filter(item => 
      item.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const timeframes = ['1m', '5m', '15m', '30m', '1H', '4H', '6H', '12H', '1D', '1W', '3W', '1M', '2M', '3M', '6M', '1Y'];

  // const chartTypes = ['line', 'area', 'ranko', 'candlestick', 'heinkshi'];
  const chartTypes = ['line', 'area', 'candlestick'];  



  // Both side panels start open. `useIsMobile` resolves the viewport on the first render, so
  // this is the real answer rather than a desktop guess corrected a frame later — small
  // screens have no room for them and keep the chart to itself.
  const [isRightPanelVisible, setIsRightPanelVisible] = useState(!isMobile);
  const [isLeftPanelVisible, setIsLeftPanelVisible] = useState(!isMobile);
  const [rightPanelOrder, setRightPanelOrder] = useState<string[]>(['orderbook', 'marketdepth', 'livepricebig', 'technical']);

  // Override panel visibility when Full Chart View is enabled
  const effectiveRightPanelVisible = isFullChartView ? false : isRightPanelVisible;
  const effectiveLeftPanelVisible = isFullChartView ? false : isLeftPanelVisible;

  // Right panel toggle function
  const toggleRightPanel = () => {
    setIsRightPanelVisible(prev => {
      const newValue = !prev;
      return newValue;
    });
  };

  // Left panel toggle function
  const toggleLeftPanel = () => {
    setIsLeftPanelVisible(prev => {
      const newValue = !prev;
      return newValue;
    });
  };

  // Make the function available globally for TradingForm to call
  useEffect(() => {
    (window as any).toggleRightPanel = toggleRightPanel;   
    return () => {
      delete (window as any).toggleRightPanel;
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up any pending animation frames
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      // Clean up event listeners
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Reset body styles
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('resizing');
    };
  }, []);

  // Memoize the price update callback to prevent infinite re-renders
  const handlePriceUpdate = useCallback((price: string, change: string, isPositive: boolean) => {
    setCurrentPrice(price);
  }, []);

  const handlePositionPanelFullscreenToggle = useCallback(() => {
    setIsPositionPanelFullscreen(prev => !prev);
  }, []);

  // Derived, not state. These used to be built by a useEffect into useState, which meant
  // the array was empty on the first render — so the chart had nothing to mount into and
  // its loader ran against a container that did not exist yet — and every dependency
  // change cost two render passes, with the chart seeing the new panel width one frame
  // after the layout had already reflowed around it.
  //
  // The chart and the positions panel are memoised separately because they do not react to
  // the same things. Sharing one array meant toggling a side panel rebuilt the chart's
  // whole element tree, so a click on the toggle had to re-render the largest component in
  // the app before the browser could paint the panel — which is what made the toggle feel
  // frozen. The chart does not read panel visibility at all; it sizes itself.
  const chartPanel = useMemo<Panel>(() => ({
    id: 'chart',
    title: 'Real Time Chart',
    component: (
      <div className="rounded-lg relative">
        <div
          className={`h-full theme-bg-primary rounded-lg relative ${
            theme === 'light' ? 'border border-gray-100' : 'border border-gray-800'
          }`}
          style={{ height: `calc(100vh - ${isPositionsExpanded ? '290px' : '172px'})` }}
        >
          <TradingChart
            symbol={selectedSymbol}
            timeframe={timeframe}
            chartType={chartType}
            onPriceUpdate={handlePriceUpdate}
            onKlinePriceUpdate={handleKlinePriceUpdate}
          />
        </div>
      </div>
    )
  }), [theme, selectedSymbol, timeframe, chartType, handlePriceUpdate, handleKlinePriceUpdate, isPositionsExpanded]);

  const positionsPanel = useMemo<Panel>(() => ({
    id: 'positions',
    title: 'Positions Panel',
    component: (
      <PositionPanel
        isRightPanelVisible={effectiveRightPanelVisible}
        onExpandedChange={setIsPositionsExpanded}
        isFullscreen={isPositionPanelFullscreen}
        onFullscreenToggle={handlePositionPanelFullscreenToggle}
        hasBeenManuallyResized={hasBeenManuallyResized}
        positionPanelHeight={positionPanelHeight}
      />
    )
  }), [effectiveRightPanelVisible, isPositionPanelFullscreen, handlePositionPanelFullscreenToggle, hasBeenManuallyResized, positionPanelHeight]);

  // Handle responsive timeframe display and arrow navigation
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 660) {
        setShowArrows(true);
        // Ensure timeframeIndex doesn't exceed bounds
        const maxIndex = Math.max(0, timeframes.length - 3);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else if (width < 1200) {
        setShowArrows(true);
        // Ensure timeframeIndex doesn't exceed bounds
        const maxIndex = Math.max(0, timeframes.length - 8);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else if (width < 1400) {
        setShowArrows(true);
        // Ensure timeframeIndex doesn't exceed bounds
        const maxIndex = Math.max(0, timeframes.length - 10);
        if (timeframeIndex > maxIndex) {
          setTimeframeIndex(maxIndex);
        }
      } else {
        setShowArrows(false);
        setTimeframeIndex(0);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [timeframeIndex, timeframes.length]);

  // Navigation functions
  const navigateTimeframe = (direction: 'left' | 'right') => {
    if (direction === 'left' && timeframeIndex > 0) {
      setTimeframeIndex(prev => prev - 1);
    } else if (direction === 'right') {
      const width = window.innerWidth;
      if (width < 660 && timeframeIndex < timeframes.length - 3) {
        setTimeframeIndex(prev => prev + 1);
      } else if (width >= 660 && width < 1200 && timeframeIndex < timeframes.length - 8) {
        setTimeframeIndex(prev => prev + 1);
      } else if (width >= 1200 && width < 1400 && timeframeIndex < timeframes.length - 10) {
        setTimeframeIndex(prev => prev + 1);
      }
    }
  };

  // Get visible timeframes based on current index
  const getVisibleTimeframes = () => {
    if (!showArrows) return timeframes;
    const width = window.innerWidth;
    if (width < 660) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 3);
    } else if (width < 1200) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 8);
    } else if (width < 1400) {
      return timeframes.slice(timeframeIndex, timeframeIndex + 10);
    }
    return timeframes;
  };

  const leftPanels = useMemo<Panel[]>(() => [
    { id: 'markets', title: 'Market Tickers', component: <MarketTicker selectedMarket={selectedMarket} onMarketChange={setSelectedMarket} onSymbolSelect={handleSymbolSelect} selectedSymbol={selectedSymbol} /> },
    { id: 'buy-sell', title: 'Buy/Sell Panel', component: <TradingForm symbol={selectedSymbol} currentPrice={currentPrice} /> }
    // Deps are exactly the props these two panels read. They previously also listed
    // priceChange, isPricePositive, priceColor and isRightPanelVisible, none of which are
    // passed here, so toggling a side panel needlessly rebuilt the market list.
  ], [selectedMarket, selectedSymbol, handleSymbolSelect, currentPrice]);

  const rightPanelComponents = useMemo<Panel[]>(() => [
    { id: 'orderbook', title: 'Order Book', component: <OrderBook selectedSymbol={selectedSymbol} currentPrice={currentPrice} priceChange={priceChange} isPricePositive={isPricePositive} priceColor={priceColor} /> },
    { id: 'marketdepth', title: 'Market Depth', component: <MarketDepth selectedSymbol={selectedSymbol} /> },
    { id: 'livepricebig', title: 'Live Price Big', component: <LivePriceBig selectedSymbol={selectedSymbol} currentPrice={currentPrice} priceChange={priceChange} isPricePositive={isPricePositive} priceColor={priceColor} isRightPanelVisible={effectiveRightPanelVisible} /> },
    { id: 'technical', title: 'Technical Sentiments', component: <TechnicalSentiments selectedSymbol={selectedSymbol} isRightPanelVisible={effectiveRightPanelVisible} /> }
  ], [selectedSymbol, currentPrice, priceChange, isPricePositive, priceColor, effectiveRightPanelVisible]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeId = active.id as string;
      const overId = over.id as string;

      // Check if we're moving between left and right panels
      const isActiveInLeft = leftPanels.some(panel => panel.id === activeId);
      const isOverInRight = rightPanelComponents.some(panel => panel.id === overId);
      const isActiveInRight = rightPanelComponents.some(panel => panel.id === activeId);
      const isOverInLeft = leftPanels.some(panel => panel.id === overId);

      if ((isActiveInLeft && isOverInRight) || (isActiveInRight && isOverInLeft)) {
        // Moving between left and right panels
        if (isActiveInLeft && isOverInRight) {
          const [movedPanel] = leftPanels.filter(panel => panel.id === activeId);
          const newLeftPanelsOrder = leftPanelsOrder.filter(id => id !== activeId);
          const newRightPanelOrder = [...rightPanelOrder, activeId];
          
          setLeftPanelsOrder(newLeftPanelsOrder);
          setRightPanelOrder(newRightPanelOrder);
        } else {
          const [movedPanel] = rightPanelComponents.filter(panel => panel.id === activeId);
          const newRightPanelOrder = rightPanelOrder.filter(id => id !== activeId);
          const newLeftPanelsOrder = [...leftPanelsOrder, activeId];
          
          setRightPanelOrder(newRightPanelOrder);
          setLeftPanelsOrder(newLeftPanelsOrder);
        }
      } else {
        // Reordering within the same side
        if (isActiveInLeft) {
          const oldIndex = leftPanelsOrder.findIndex(id => id === activeId);
          const newIndex = leftPanelsOrder.findIndex(id => id === overId);
          setLeftPanelsOrder(arrayMove(leftPanelsOrder, oldIndex, newIndex));
        } else if (isActiveInRight) {
          const oldIndex = rightPanelOrder.findIndex(id => id === activeId);
          const newIndex = rightPanelOrder.findIndex(id => id === overId);
          setRightPanelOrder(arrayMove(rightPanelOrder, oldIndex, newIndex));
        }
      }
    }
  };

  // Function to get asset name from symbol
  const getAssetName = (symbol: string): string => {
    const assetNames: { [key: string]: string } = {
      'EURUSD': 'EURUSD',
      'GBPUSD': 'Pound',
      'USDJPY': 'Yen',
      'USDCHF': 'Franc',
      'AUDUSD': 'Aussie',
      'USDCAD': 'Loonie',
      'NZDUSD': 'Kiwi',
      'EURGBP': 'Euro/Pound',
      'EURJPY': 'Euro/Yen',
      'GBPJPY': 'Pound/Yen',
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'ADA': 'Cardano',
      'DOT': 'Polkadot',
      'LINK': 'Chainlink',
      'LTC': 'Litecoin',
      'XRP': 'Ripple',
      'GOLD': 'Gold',
      'SILVER': 'Silver',
      'OIL': 'Oil',
      'AAPL': 'Apple',
      'GOOGL': 'Google',
      'MSFT': 'Microsoft',
      'AMZN': 'Amazon',
      'TSLA': 'Tesla',
      'META': 'Meta',
      'NVDA': 'NVIDIA',
      'NFLX': 'Netflix',
    };
    
    return assetNames[symbol] || symbol;
  };

  return (
    <div className="h-screen theme-bg-primary overflow-hidden">
      {/* Toast Container */}
      <ToastContainer />
      
      {/* Mobile Layout - Single column with specific sequence.
          Mounted only below the md breakpoint. It used to be permanently mounted and merely
          hidden with `md:hidden`, which meant desktop users were running a second copy of
          the chart, market ticker, order book, market depth and position panel — each with
          its own websocket subscriptions, intervals and REST polling — behind
          `display: none`. That doubled the app's live data cost while being invisible, and
          the offscreen chart sized itself against a zero-width container. */}
      {isMobile && (
      <div className="h-screen overflow-y-auto">
        <div className="space-y-3 p-2">
          {/* 1. Market Tickers */}
          <div className="theme-bg-primary rounded-lg p-2">
            <MarketTicker 
              selectedMarket={selectedMarket} 
              onMarketChange={setSelectedMarket} 
              onSymbolSelect={handleSymbolSelect}
              selectedSymbol={selectedSymbol}
            />
          </div>

          {/* 3. Timeframe Panel */}
          <div className="theme-bg-primary rounded-lg p-2">
            <TimeframePanel
              selectedTimeframe={timeframe}
              onTimeframeChange={setTimeframe}
              selectedChartType={chartType}
              onChartTypeChange={setChartType}
              isRightPanelVisible={effectiveRightPanelVisible}
            />
          </div>

          {/* 4. Chart */}
          <div className="theme-bg-primary rounded-lg border theme-border" style={{ height: '300px' }}>
            <TradingChart
              symbol={selectedSymbol}
              timeframe={timeframe}
              chartType={chartType}
              onPriceUpdate={handlePriceUpdate}
              onKlinePriceUpdate={handleKlinePriceUpdate}
            />
          </div>

          {/* 1.5. Live Price - Only for mobile */}
          <div className="theme-bg-primary rounded-lg ">
            <LivePrice
              selectedSymbol={selectedSymbol}
              currentPrice={currentPrice}
              priceChange={priceChange}
              isPricePositive={isPricePositive}
              priceColor={priceColor}
              isRightPanelVisible={effectiveRightPanelVisible}
            />
          </div>

          


          {/* 2. Trading Form - Hidden on mobile when full chart view is enabled */}
          {/* {!isFullChartView && ( */}
            <div className="theme-bg-primary rounded-lg p-2 border theme-border">
              <TradingForm symbol={selectedSymbol} currentPrice={currentPrice} />
            </div>
          {/* )} */}

          {/* 5. Position Panel - Hidden on mobile when full chart view is enabled */}
          {!isFullChartView && (
            <div className="theme-bg-primary rounded-lg border theme-border">
              <PositionPanel 
                isRightPanelVisible={effectiveRightPanelVisible} 
                onExpandedChange={setIsPositionsExpanded}
                isFullscreen={isPositionPanelFullscreen}
                onFullscreenToggle={() => {
                  setIsPositionPanelFullscreen(!isPositionPanelFullscreen);
                }}
                hasBeenManuallyResized={hasBeenManuallyResized}
                positionPanelHeight={positionPanelHeight}
                isMobile={true}
              />
            </div>
          )}

          {/* 6. Order Book - Hidden on mobile and when full chart view is enabled */}
          {!isFullChartView && (
            <div className="hidden md:block theme-bg-primary rounded-lg border theme-border overflow-hidden" style={{ height: '400px' }}>
              <OrderBook
                selectedSymbol={selectedSymbol}
                currentPrice={currentPrice}
                priceChange={priceChange}
                isPricePositive={isPricePositive}
                priceColor={priceColor}
              />
            </div>
          )}

          {/* 7. Market Depth - Hidden on mobile and when full chart view is enabled */}
          {!isFullChartView && (
            <div className="hidden md:block theme-bg-primary rounded-lg border theme-border overflow-hidden" style={{ height: '120px' }}>
              <MarketDepth selectedSymbol={selectedSymbol} />
            </div>
          )}

          {/* 8. Sentiments  - do not delete this*/}
          {/* <div className="theme-bg-primary rounded-lg border theme-border overflow-hidden" style={{ height: '290px' }}>
            <TechnicalSentiments isRightPanelVisible={effectiveRightPanelVisible} />
          </div> */}
        </div>
      </div>
      )}

      {/* Desktop Layout - Two Column Layout. Mounted only at md and above, for the same
          reason as the mobile tree above. */}
      {!isMobile && (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-screen">
          {/* Left Panel - Only show on left when not in right position mode */}
          {!isPanelOnRight && (
            <LeftPanel
              isLeftPanelVisible={effectiveLeftPanelVisible}
              onClose={toggleLeftPanel}
              leftPanels={leftPanels}
              leftPanelsOrder={leftPanelsOrder}
            />
          )}

          {/* Main Content Area - Flexible width */}
          <div
            className={`flex-1 theme-bg-primary ${!isPanelOnRight && effectiveLeftPanelVisible ? 'pl-2' : 'pl-0'} ${isPanelOnRight && effectiveLeftPanelVisible ? 'pr-2' : 'pr-0'} pt-0 pb-[60px] h-screen flex flex-col relative`}
            style={{
              transition: 'padding-left 300ms ease-in-out, padding-right 300ms ease-in-out',
              maxWidth: '100%',
              // A flex item's automatic minimum size is its content width, which lets this
              // column refuse to shrink and push the right panel off screen. maxWidth alone
              // does not override that; both bounds together keep it inside the row.
              minWidth: 0,
            }}
       
          >
            {/* Left Panel Toggle Button - Only visible when left panel is closed and Full Chart View is disabled */}
            {!effectiveLeftPanelVisible && !isFullChartView && (
              <button
                onClick={toggleLeftPanel}
                className={`absolute top-2 z-40 p-1 rounded-lg transition-all duration-200 shadow-xs border theme-bg-quaternary hover:theme-bg-quinary theme-text-secondary hover:text-[#2AB7A6] theme-border group ${isPanelOnRight ? 'right-2' : 'left-2'}`}
                title={`Open ${isPanelOnRight ? 'right' : 'left'} panel`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isPanelOnRight ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7m8 14l-7-7 7-7"} className="group-hover:stroke-[#2AB7A6] transition-colors duration-200" />
                </svg>
              </button>
            )}
            
            {/* Content Area - Chart and Positions on left, Right Panel on right */}
            <div className="flex flex-1 min-h-0">
              {/* Left Content - Chart and Positions */}
              <div
                className={`flex flex-col relative flex-1`}
                style={{ 
                  height: 'calc(100vh - 60px)',
                  paddingRight: effectiveRightPanelVisible ? '8px' : '0px',
                  minWidth: '0', // Prevent flex item from growing beyond container
                  transition: 'padding-right 300ms ease-in-out'
                }}
              >
                {/* Right Panel Toggle Button - Only visible when right panel is closed and Full Chart View is disabled */}
                {!effectiveRightPanelVisible && !isFullChartView && (
                  <button
                    onClick={toggleRightPanel}
                    className="absolute top-2 right-2 z-40 p-1 rounded-lg transition-all duration-200 shadow-xs border theme-bg-quaternary hover:theme-bg-quinary theme-text-secondary hover:text-[#2AB7A6] theme-border group"
                    title="Open right panel"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" className="group-hover:stroke-[#2AB7A6] transition-colors duration-200" />
                    </svg>
                  </button>
                )}
                {/* Timeframe Header - Now in middle panel */}
                <div className={`flex flex-col xl:flex-row xl:items-center xl:justify-between mb-2 space-y-2 xl:space-y-0 ${isFullChartView ? 'sm:flex-row sm:items-center sm:justify-between sm:space-y-0 flex-nowrap' : ''}`}>
                  {/* Left Side - Timeframe and Chart Type Options */}
                  
                  <div className={`p-0 m-0 ${!effectiveLeftPanelVisible && !isFullChartView ? 'ml-12' : ''} ${isFullChartView ? 'flex-shrink-0 min-w-0' : ''}`}> 
                  <TimeframePanel
                    selectedTimeframe={timeframe}
                    onTimeframeChange={setTimeframe}
                    selectedChartType={chartType}
                    onChartTypeChange={setChartType}
                    isRightPanelVisible={effectiveRightPanelVisible}
                    isFullChartView={isFullChartView}
                  />
                  </div>

                  {/* Right Side - Symbol and Real-time Price */}
                  <div className={`p-0 m-0 ${!effectiveRightPanelVisible && !isFullChartView ? 'mr-12' : ''} ${isFullChartView ? 'flex-shrink-0 min-w-0' : ''}`}> 
  {/* isRightPanelVisible &&  */}
                  {(
                    <LivePriceRight
                      selectedSymbol={selectedSymbol}
                      currentPrice={currentPrice}
                      priceChange={priceChange}
                      isPricePositive={isPricePositive}
                      priceColor={priceColor}
                      isLeftPanelVisible={effectiveLeftPanelVisible}
                      onSymbolChange={handleSymbolSelect}
                    />
                  )}                      
                  </div>
                </div>

                {/* Chart Panel - Hidden in fullscreen mode */}
                {!isPositionPanelFullscreen && (
                  <div 
                    className="flex-1 min-h-0 relative"
                    style={{ 
                      height: isDragging 
                        ? `calc(100% - 1px - ${positionPanelHeight}px)`
                        : `calc(100% - 1px - ${calculateHeights.positionHeight})`,
                      zIndex: isPositionPanelFullscreen ? 30 : 10, // Lower z-index for chart than position panel
                      transition: isDragging ? 'none' : 'all 300ms ease-in-out' // Disable transition during drag
                    }}
                  >
                    <SortablePanel panel={chartPanel} />
                  </div>
                )}
                
                {/* Resize Handle - Hidden in fullscreen mode */}
                {!isPositionPanelFullscreen && (
                  <div
                    className={`resize-handle h-0.5 cursor-ns-resize transition-colors duration-200 flex items-center justify-center relative group ${
                      theme === 'light' 
                        ? 'bg-gray-100 hover:bg-gray-200' 
                        : 'bg-gray-800 hover:bg-gray-700'
                    } ${isDragging ? 'bg-blue-200 dark:bg-blue-800' : ''}`}
                    style={{ zIndex: 15 }} // Middle z-index for resize handle, between chart and position panel
                    onMouseDown={handleMouseDown}
                  >
                    <div className={`w-12 h-1 rounded ${
                      theme === 'light' 
                        ? 'bg-gray-400 group-hover:bg-gray-500' 
                        : 'bg-gray-600 group-hover:bg-gray-500'
                    } ${isDragging ? 'bg-blue-500' : ''}`}></div>
                  </div>
                )}
                
                {/* Positions Panel - Full height in fullscreen, normal height otherwise */}
                <div 
                  className={`relative ${
                    isPositionPanelFullscreen ? 'absolute top-0 left-0 right-0 bottom-0' : ''
                  }`}
                  style={{ 
                    height: isDragging 
                      ? `${positionPanelHeight}px`
                      : calculateHeights.positionHeight,
                    maxHeight: isDragging 
                      ? `${positionPanelHeight}px`
                      : calculateHeights.positionMaxHeight,
                    zIndex: isPositionPanelFullscreen ? 40 : 20,
                    transition: isDragging ? 'none' : 'all 300ms ease-in-out' // Disable transition during drag
                  }}
                >
                  <SortablePanel panel={positionsPanel} />
                </div>
              </div>

              {/* Right Panel - Individual Sortable Components - Conditionally visible */}
              {effectiveRightPanelVisible && (
                <div className="group/panel w-64 min-w-64 max-w-64 theme-bg-primary border-l theme-border px-2 pb-[60px] overflow-y-auto scrollbar-hide space-y-2 relative" style={{ height: 'calc(100vh - 0px)', zIndex: '21' }}>
                  {/* Close Button - Only visible while the panel is hovered. The group is named so
                      it does not also drive the hover-revealed controls the panels inside carry. */}
                  <div className="flex items-center justify-end mb-0 absolute top-1 right-2 z-30 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/panel:opacity-100 group-hover/panel:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto" style={{ top: '7px' }}>
                    <button
                      onClick={() => setIsRightPanelVisible(false)}
                      className="p-1 rounded-sm transition-all duration-200"
                      title="Close panel"
                    >
                      <X className="h-3 w-3 theme-text-quaternary group-hover:text-white transition-colors duration-200" />
                    </button>
                  </div>
                  
                  <SortableContext
                    items={rightPanelOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    {rightPanelOrder.map((panelId) => {
                      const panel = rightPanelComponents.find(p => p.id === panelId);
                      return panel ? <SortablePanel key={panel.id} panel={panel} /> : null;
                    })}
                  </SortableContext>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Only show on right when in right position mode */}
          {isPanelOnRight && (
            <LeftPanel
              isLeftPanelVisible={effectiveLeftPanelVisible}
              onClose={toggleLeftPanel}
              leftPanels={leftPanels}
              leftPanelsOrder={leftPanelsOrder}
            />
          )}
        </div>
      </DndContext>
      )}
    </div>
  );
};

export default ReorderableLayout;
